import type { INodeDto } from '@/types/INodeDto';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { MutableRefObject } from 'react';
import ReactDOM from 'react-dom';
import { TreeRow } from './TreeBuilder';

/** Assignable node types for material assignment */
export type AssignableRowType = 'element' | 'component' | 'supplemental';

/** Array of assignable row types for filtering */
const ASSIGNABLE_TYPES: AssignableRowType[] = ['element', 'component', 'supplemental'];

/** Extended TreeRow with optional change tracking */
export interface TrackedTreeRow extends TreeRow { }

/**
 * Abstracts MUI DataGrid apiRef operations for material assignment workflows.
 * Provides a clean interface for row selection, access, and updates without
 * exposing MUI DataGrid internals to consuming services.
 */
export class GridProvider {
  private apiRef: MutableRefObject<GridApiPremium>;

  /**
   * Creates a new GridProvider.
   * @param apiRef Reference to the MUI DataGrid Premium API
   */
  constructor(apiRef: MutableRefObject<GridApiPremium>) {
    this.apiRef = apiRef;
  }

  /**
   * Gets all currently selected rows from the grid.
   * @returns Map of row id to TreeRow
   */
  getSelectedRows(): Map<string, TreeRow> {
    const selectedMap = this.apiRef.current?.getSelectedRows();
    if (!selectedMap) {
      return new Map();
    }
    // Convert to TreeRow map (the grid stores GridValidRowModel)
    const result = new Map<string, TreeRow>();
    selectedMap.forEach((row, id) => {
      result.set(String(id), row as TreeRow);
    });
    return result;
  }

  /**
   * Gets selected rows filtered to assignable types (element, component, supplemental)
   * that have INodeDto data.
   * @returns Array of TreeRow items that can have materials assigned
   */
  getSelectedAssignableRows(): TreeRow[] {
    const selectedRows = this.getSelectedRows();
    const assignableRows: TreeRow[] = [];

    selectedRows.forEach((row) => {
      if (this.isAssignableRow(row)) {
        assignableRows.push(row);
      }
    });

    return assignableRows;
  }

  /**
   * Gets node (INodeDto) objects from selected assignable rows.
   * @returns Array of INodeDto from the selection
   */
  getSelectedNodeDtos(): INodeDto[] {
    const assignableRows = this.getSelectedAssignableRows();
    return assignableRows
      .filter((row) => row.data !== undefined)
      .map((row) => row.data!);
  }

  /**
   * Gets a row by its ID.
   * @param id The row ID
   * @returns The TreeRow or undefined if not found
   */
  getRowById(id: string): TreeRow | undefined {
    try {
      const row = this.apiRef.current?.getRow?.(id);
      return row as TreeRow | undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Gets all rows from the grid.
   * @returns Array of all TreeRow items
   */
  getAllRows(): TreeRow[] {
    const rows: TreeRow[] = [];
    const rowIds = this.apiRef.current?.getAllRowIds?.() || [];

    for (const id of rowIds) {
      const row = this.getRowById(String(id));
      if (row) {
        rows.push(row);
      }
    }

    return rows;
  }

  /**
   * Updates data for a specific row.
   * @param rowId The row ID to update
   * @param updates Partial updates to apply to the row
   */
  updateRowData(rowId: string, updates: Partial<TrackedTreeRow>): void {
    const currentRow = this.getRowById(rowId);
    if (!currentRow) {
      return;
    }

    const updatedRow = { ...currentRow, ...updates };
    this.apiRef.current?.updateRows?.([updatedRow]);
  }

  /**
   * Lightweight refresh for recalculating aggregations and re-rendering rows.
   */
  refreshRows(): void {
    this.apiRef.current?.unstable_applyFilters?.();
  }

  /**
   * Clears change markers from the specified rows.
   * Deprecated in favor of refreshRows since row state is no longer stored.
   */
  clearRowChanges(_rowIds: string[]): void {
    this.refreshRows();
  }

  /**
   * Batch updates multiple rows in the grid.
   * Uses ReactDOM.flushSync for consistent rendering.
   * @param rows Array of TreeRow items to update
   * @param onComplete Optional callback after update completes
   */
  updateRows(rows: TreeRow[], onComplete?: () => void): void {
    queueMicrotask(() => {
      ReactDOM.flushSync(() => {
        this.apiRef.current?.setRows?.(rows);
        this.apiRef.current?.unstable_applyFilters?.();
      });
      onComplete?.();
    });
  }

  /**
   * Checks if a row is an assignable type with data.
   * @param row The row to check
   * @returns True if the row can have materials assigned
   */
  isAssignableRow(row: TreeRow): boolean {
    return (
      ASSIGNABLE_TYPES.includes(row.type as AssignableRowType) &&
      row.data !== undefined
    );
  }

  /**
   * Gets the count of selected assignable rows.
   * @returns Number of selected rows that can have materials assigned
   */
  getSelectedAssignableCount(): number {
    return this.getSelectedAssignableRows().length;
  }

  /**
   * Clears the current row selection.
   */
  clearSelection(): void {
    this.apiRef.current?.setRowSelectionModel?.([]);
  }

  /**
   * Checks if the grid API is available.
   * @returns True if the API reference is valid and initialized
   */
  isReady(): boolean {
    return this.apiRef.current !== null && this.apiRef.current !== undefined;
  }
}
